import { useState } from "react";
import { Download } from "lucide-react";
import { usePlatformCapabilities } from "@shared-core/capabilities/context";
import { useObjectUiAdapter } from "@shared-core/data/ObjectUiAdapterContext";
import {
  archivedArtworkOf,
  type ArchivedArtworkFile,
  type ArchivedArtworkJob,
} from "@shared-core/domain/archived-artwork";
import { formatBytes } from "@shared-core/domain/formatters";
import type { RecordView } from "@shared-core/domain/object-ui/contract";
import { Button } from "../../primitives/button";
import { Card, CardContent } from "../../primitives/card";

/**
 * The Archived artwork section of a Tharstern order's record (One package T3): for each Tharstern job of the order,
 * the files the Tharstern artwork archive holds, where Tharstern said each was filed, its size and a Download. A job
 * the archive holds nothing for says "none filed" or "not archived yet" in the backend's words. Read-only: reusing a
 * file is the order's "Use archived artwork" choice. Renders nothing while the record carries no section (the archive's
 * feature is off for the person, they lack its permit, or the order names no job). One-only: a custom section on
 * `orders.tharstern_order`.
 */
export function ArchivedArtworkSection({ record }: { record: RecordView }) {
  const jobs = archivedArtworkOf(record);
  if (jobs.length === 0) return null;
  return (
    <Card data-section-id="tharstern_order.archived_artwork">
      <CardContent className="space-y-3 py-4 max-sm:px-4">
        <h2 className="text-base font-semibold">Archived artwork</h2>
        {jobs.map((job) => (
          <JobArchive key={job.jobNo} job={job} />
        ))}
      </CardContent>
    </Card>
  );
}

function JobArchive({ job }: { job: ArchivedArtworkJob }) {
  return (
    <div className="space-y-1.5 border-t pt-3 first:border-t-0 first:pt-0">
      <h3 className="text-sm font-medium">{job.heading}</h3>
      {job.statusText && (
        <p className="text-sm text-muted-foreground">{job.statusText}</p>
      )}
      {job.files.length > 0 && (
        <ul className="space-y-1.5">
          {job.files.map((file, n) => (
            <li key={`${file.origin}-${file.fileName}-${n}`}>
              <FileRow file={file} />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function FileRow({ file }: { file: ArchivedArtworkFile }) {
  const name = file.fileName ?? "Unnamed file";
  return (
    <div className="space-y-0.5 text-sm">
      <div className="flex flex-wrap items-center gap-x-2">
        <span className="break-all font-medium">{name}</span>
        {file.sizeBytes !== null && (
          <span className="text-muted-foreground">
            {formatBytes(file.sizeBytes)}
          </span>
        )}
        {file.downloadHref ? (
          <DownloadButton href={file.downloadHref} name={name} />
        ) : (
          file.statusText && (
            <span className="text-muted-foreground">{file.statusText}</span>
          )
        )}
      </div>
      <p className="text-xs text-muted-foreground">{file.origin}</p>
    </div>
  );
}

/**
 * The archived copy at the archive's own route, fetched with the person's sign-in and saved: a plain link would reach
 * the API without it. The archive serves it as an attachment, never rendered in the page.
 */
function DownloadButton({ href, name }: { href: string; name: string }) {
  const adapter = useObjectUiAdapter();
  const capabilities = usePlatformCapabilities();
  const [hasFailed, setHasFailed] = useState(false);
  const save = async () => {
    setHasFailed(false);
    try {
      const { blob, filename } = await adapter.download(href);
      capabilities.downloadBlob(filename || name, blob);
    } catch {
      console.warn("object_ui.archived_artwork.download_failed");
      setHasFailed(true);
    }
  };
  return (
    <>
      <Button
        size="sm"
        variant="ghost"
        className="h-7 px-2"
        aria-label={`Download ${name}`}
        onClick={() => void save()}
      >
        <Download aria-hidden className="size-3.5" />
        Download
      </Button>
      {hasFailed && (
        <span role="alert" className="text-xs text-red-600">
          {name} couldn't be downloaded.
        </span>
      )}
    </>
  );
}
